import React, { useEffect, useState, useRef, useCallback } from 'react';
import MainCard from 'ui-component/cards/MainCard';
import {
    Autocomplete,
    Button,
    CardContent,
    Grid,
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    TableBody,
    Stack,
    IconButton,
    CircularProgress,
    Tooltip,
    Pagination,
    MenuItem
} from '@mui/material';
import { gridSpacing } from 'store/constant';
import { GroupType } from 'types/sacp';
import { useNavigate, useParams } from 'react-router-dom';
import LibraryAddOutlinedIcon from '@mui/icons-material/LibraryAddOutlined';
import { useTheme } from '@mui/material/styles';
import { getGroupList } from 'services/sacp';
import Loader from 'ui-component/Loader';
import useGetLocationList from 'hooks/consume_api/query/useGetLocationList';
import BorderColorTwoToneIcon from '@mui/icons-material/BorderColorTwoTone';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import useAuth from 'hooks/useAuth';
import DeleteIcon from '@material-ui/icons/Delete';
import useGroupDelete from 'hooks/consume_api/mutation/useGroupDelete';
import GroupDeleteModal from './GroupDeleteModal';  // Fixed import
import { initiateGroupExport } from 'services/reactQueryservices';
import SearchIcon from '@mui/icons-material/Search';
import useUserPermission from 'hooks/consume_api/query/useUserPermission';
import { isExportInProgress, runTrackedExport, subscribeToExportStatus } from '../exportStatus';

interface FieldOption {
    id: number;
    field_name: string;
    field_parent_id: number;
    field_type_id: number;
    geocode: string;
    geojson: object;
    uploaded_file_path: string;
    created_by: number;
    updated_by: number;
    created_date: string;
    updated_date: string;
    longitude: number | null;
    latitude: number | null;
    field_name_bn: string | null;
}

interface PermissionData {
    role_id: string;
    action_id: string;
    permission: boolean;
    module_id: string;
    module_name: string;
    action_name: string;
}

interface ProjectTypeOption {
    name: string;
}

const projectTypeOptions: ProjectTypeOption[] = [{ name: 'SACP' }, { name: 'RAINS' }];

const GroupRegister = () => {
    const theme = useTheme();
    const { id: projectId } = useParams();
    const navigate = useNavigate();

    // Refs for cleanup and tracking
    const isMountedRef = useRef(true);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const permissionRequestRef = useRef<boolean>(false);

    const [selectedData, setSelectedData] = useState<GroupType | null>(null);
    const [deletePermission, setDeletePermission] = useState<boolean>(false);
    const [data, setData] = useState<any>();
    const [isDelete, setIsDelete] = useState<boolean>(false);
    const [row, setRow] = useState<number>(30);
    const [search, setSearch] = useState<string>('');
    const [jumpPage, setJumpPage] = useState<string>('');
    const [finalSearch, setFinalSearch] = useState<string>('');

    const [openModal, setOpenModal] = useState<boolean>(false);
    const [groupId, setGroupId] = useState<number>(0);

    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);
    const enableDelete = () => setIsDelete(!isDelete);

    // Permission states - SECURE BY DEFAULT
    const [canAddGroup, setCanAddGroup] = useState<boolean>(false);
    const [canEditGroup, setCanEditGroup] = useState<boolean>(false);
    const [canViewGroup, setCanViewGroup] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState<boolean>(false);

    // Navigation state to prevent rapid clicks
    const [isNavigating, setIsNavigating] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const groupExportStatusKey = `sacp-group-export:${projectId || ''}`;

    const { mutate: deleteGroup, isSuccess } = useGroupDelete();

    const [page, setPage] = useState<number>(1);

    const { user } = useAuth();
    const { mutate: fetchPermissions, actionData } = useUserPermission();

    const [isFetching, setIsFetching] = useState<boolean>(false);

    const formId = process.env.REACT_APP_GROUP_FORM_ID;
    const xformId = process.env.REACT_APP_GROUP_XFORM_ID;

    const submissionUrl = `/project/${projectId}/form/${formId}/${xformId}/new-submission`;
    const editUrl = `/project/${projectId}/form/${formId}/${xformId}/edit-submission`;
    const profileUrl = `/project/${projectId}/sacp/group`;

    const [location, setLocation] = useState<string>('-1');
    const [divisionId, setDivisionId] = useState<string>('');
    const [districtId, setDistrictId] = useState<string>('');
    const [upazilaId, setUpazilaId] = useState<string>('');

    const [unionId, setUnionId] = useState<string>('');
    const [projectTypeName, setProjectTypeName] = useState<string>('');

    const { data: divisionData } = useGetLocationList(location);
    const { data: distirctData } = useGetLocationList(divisionId);
    const { data: upazilaData } = useGetLocationList(districtId);
    const { data: unionData } = useGetLocationList(upazilaId);

    // Cleanup on component unmount
    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
            // Cleanup timers
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            // Reset all states
            setCanAddGroup(false);
            setCanEditGroup(false);
            setCanViewGroup(false);
            setDeletePermission(false);
            setIsInitialized(false);
            setHasInitialDataLoaded(false);
            setIsNavigating(false);
            permissionRequestRef.current = false;
        };
    }, []);

    useEffect(() => {
        const syncExportStatus = () => {
            if (isMountedRef.current) {
                setIsExporting(isExportInProgress(groupExportStatusKey));
            }
        };

        syncExportStatus();

        return subscribeToExportStatus(groupExportStatusKey, syncExportStatus);
    }, [groupExportStatusKey]);

    const fetchData = async (
        page: number,
        limit: number,
        division_id: string,
        district_id: string,
        upazila_id: string,
        union_id: string,
        search: string,
        project_type_name: string
    ) => {
        setIsFetching(true);
        const groupData = await getGroupList({
            page,
            limit,
            division_id,
            district_id,
            upazila_id,
            union_id,
            firstName: search,
            project_type: project_type_name,
            project_type_name
        });
        setIsFetching(false);
        if (isMountedRef.current) {
            setData(groupData);
            if (!hasInitialDataLoaded) {
                setHasInitialDataLoaded(true);
            }
        }
    };

    // Debounced permission fetching function
    const fetchGroupPermissions = useCallback(() => {
        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;

            if (user?.roles && !permissionRequestRef.current) {
                permissionRequestRef.current = true;
                const roleIds = user.roles.map((role: any) => role.id);
                console.log('Fetching group permissions for roles:', roleIds);
                fetchPermissions(roleIds);
            } else if (!user?.roles) {
                // If no user, set initialized with default values
                if (isMountedRef.current) {
                    setIsInitialized(true);
                }
            }
        }, 150); // 150ms debounce to prevent rapid calls
    }, [user, fetchPermissions]);

    // Fetch permissions when component mounts
    useEffect(() => {
        if (isMountedRef.current) {
            fetchGroupPermissions();
        }

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [fetchGroupPermissions]);

    // Process permission data with NEW LOGIC
    useEffect(() => {
        if (actionData && isMountedRef.current) {
            console.log('Group permission data received:', actionData);

            // Reset permission request flag
            permissionRequestRef.current = false;

            // Check for Group Register module permissions
            const groupPermissions = actionData.filter(
                (permission: PermissionData) => permission.module_name === 'Group Register'
            );

            console.log('Group Register permissions found:', groupPermissions);

            // NEW LOGIC BASED ON YOUR API RESPONSE PATTERNS

            // Case 1: No permissions at all for this module
            if (groupPermissions.length === 0) {
                console.log('No permissions found for Group Register module - granting full access');
                setCanAddGroup(true);
                setCanEditGroup(true);
                setCanViewGroup(true);
            }
            // Case 2: Only View permission exists (like Admin role 88)
            else if (groupPermissions.length === 1 && groupPermissions[0].action_name === 'View') {
                console.log('Only View permission found for Group Register - granting full access');
                setCanAddGroup(true);
                setCanEditGroup(true);
                setCanViewGroup(groupPermissions[0].permission);
            }
            // Case 3: Multiple permissions exist (like role 91)
            else {
                // Find specific permissions
                const viewPermission = groupPermissions.find(
                    (perm: PermissionData) => perm.action_name === 'View'
                );
                const addPermission = groupPermissions.find(
                    (perm: PermissionData) => perm.action_name === 'Add'
                );
                const editPermission = groupPermissions.find(
                    (perm: PermissionData) => perm.action_name === 'Edit'
                );

                console.log('Specific permissions found:', {
                    view: viewPermission?.permission,
                    add: addPermission?.permission,
                    edit: editPermission?.permission
                });

                // Set permissions based on API data
                setCanViewGroup(viewPermission ? viewPermission.permission : false);
                setCanAddGroup(addPermission ? addPermission.permission : false);
                setCanEditGroup(editPermission ? editPermission.permission : false);
            }

            // Always check for admin role as a safety fallback
            const isAdmin = user?.roles?.some((item: any) =>
                item.name === 'FAO_ADMIN' || item.name === 'ADMIN'
            );

            if (isAdmin) {
                console.log('Admin user detected - ensuring full access');
                setCanAddGroup(true);
                setCanEditGroup(true);
                setCanViewGroup(true);
            }

            // Mark permissions as initialized
            if (isMountedRef.current) {
                setIsInitialized(true);
            }
        }
    }, [actionData, user]);

    // Set delete permission based on FAO_ADMIN role
    useEffect(() => {
        if (isMountedRef.current && user) {
            const isFaoAdmin = user.roles.some((item: any) => item.name === 'FAO_ADMIN');
            setDeletePermission(isFaoAdmin);
        }
    }, [user]);

    // Helper function to verify permissions directly from user roles
    const verifyUserPermission = useCallback((action: 'add' | 'edit' | 'delete' | 'view'): boolean => {
        if (!user?.roles) return false;

        // Check if user is admin
        const isAdmin = user.roles.some((role: any) =>
            role.name === 'FAO_ADMIN' || role.name === 'ADMIN'
        );

        if (isAdmin) {
            console.log('Admin user detected - granting group permission for:', action);
            return true;
        }

        // For non-admin users, use state permissions
        switch (action) {
            case 'add':
                return canAddGroup;
            case 'edit':
                return canEditGroup;
            case 'delete':
                return deletePermission;
            case 'view':
                return canViewGroup;
            default:
                return false;
        }
    }, [user, canAddGroup, canEditGroup, deletePermission, canViewGroup]);

    // Secure add group function with double verification
    const handleAddClick = () => {
        // Prevent multiple rapid clicks
        if (isNavigating) {
            console.log('Navigation already in progress');
            return;
        }

        setIsNavigating(true);

        // Double-check permissions before navigating
        const hasPermission = verifyUserPermission('add');

        if (!hasPermission) {
            console.warn('Permission denied for adding groups. User roles:', user?.roles);
            setIsNavigating(false);
            return;
        }

        console.log('Permission granted for adding group. Navigating...');

        // Small delay to ensure state consistency
        setTimeout(() => {
            if (isMountedRef.current) {
                navigate(submissionUrl);
            }
            setIsNavigating(false);
        }, 50);
    };

    // Secure edit group function
    const handleEditClick = (itemId: string) => {
        if (isNavigating) return;

        setIsNavigating(true);

        const hasPermission = verifyUserPermission('edit');

        if (!hasPermission) {
            console.warn('Permission denied for editing groups');
            setIsNavigating(false);
            return;
        }

        setTimeout(() => {
            if (isMountedRef.current) {
                navigate(`${editUrl}/${itemId}`);
            }
            setIsNavigating(false);
        }, 50);
    };

    const handleDelete = (id: number) => {
        const hasPermission = verifyUserPermission('delete');

        if (!hasPermission) {
            console.warn('Permission denied for deleting groups');
            return;
        }

        setGroupId(id);
        handleOpenModal();
    };

    const handleChangePage = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage);
    };

    const handleRowChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setRow(Number(event.target.value));
    };

    const handleSearch = () => {
        setFinalSearch(search);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Fetch data when dependencies change
    useEffect(() => {
        if (isMountedRef.current && isInitialized && canViewGroup) {
            fetchData(page, row, divisionId, districtId, upazilaId, unionId, finalSearch.toLowerCase(), projectTypeName);
        }
    }, [page, divisionId, districtId, upazilaId, unionId, isDelete, row, finalSearch, isInitialized, canViewGroup, projectTypeName]);

    useEffect(() => {
        if (isMountedRef.current && isSuccess) {
            setIsDelete(!isDelete);
        }
    }, [isSuccess]);

    const handleExport = async () => {
        if (isExportInProgress(groupExportStatusKey)) {
            setIsExporting(true);
            return;
        }

        try {
            setIsExporting(true);
            await runTrackedExport(groupExportStatusKey, () =>
                initiateGroupExport(divisionId, districtId, upazilaId, unionId, finalSearch, projectTypeName)
            );
        } catch (error) {
            console.error('Group export failed:', error);
        } finally {
            if (isMountedRef.current) {
                setIsExporting(isExportInProgress(groupExportStatusKey));
            }
        }
    };

    // Debug logging
    useEffect(() => {
        if (isMountedRef.current) {
            console.log('Group Permission State:', {
                isInitialized,
                canAddGroup,
                canEditGroup,
                canViewGroup,
                deletePermission,
                hasInitialDataLoaded,
                userRoles: user?.roles?.map((r: any) => r.name)
            });
        }
    }, [isInitialized, canAddGroup, canEditGroup, canViewGroup, deletePermission, hasInitialDataLoaded, user]);

    // Show loader until permissions AND initial data are resolved
    if (!isInitialized || !hasInitialDataLoaded) {
        console.log('Showing loader for GroupRegister - Waiting for:', {
            permissions: !isInitialized,
            data: !hasInitialDataLoaded
        });
        return <Loader />;
    }

    // Check if user has view permission
    if (!canViewGroup) {
        return (
            <MainCard title="Access Denied">
                <CardContent>
                    <Typography variant="body1" color="error">
                        You do not have permission to view Group Register.
                    </Typography>
                </CardContent>
            </MainCard>
        );
    }

    console.log('Rendering Group MainCard with permissions:', {
        canAddGroup,
        canEditGroup,
        canViewGroup,
        deletePermission
    });

    return (
        <MainCard
            content={false}
            contentSX={{ padding: 0 }}
            sx={{ padding: 0 }}
            title={
                <Grid container alignItems="center" justifyContent="space-between" spacing={gridSpacing}>
                    <Grid item>
                        <Typography variant="h3">Group List</Typography>
                    </Grid>
                    <Grid item>
                        {/* Show Add New Group button only if verified permissions */}
                        {canAddGroup && (
                            <Button
                                sx={{ ml: -1 }}
                                color="secondary"
                                variant="contained"
                                onClick={handleAddClick}
                                disabled={isNavigating}
                                startIcon={<LibraryAddOutlinedIcon fontSize="small" />}
                            >
                                {isNavigating ? 'Processing...' : 'Add New Group'}
                            </Button>
                        )}

                        {/* Export button - always visible if user can view */}
                        {canViewGroup && (
                            <Button
                                sx={{ ml: 1 }}
                                color="secondary"
                                variant="contained"
                                onClick={handleExport}
                                disabled={isNavigating || isExporting}
                                startIcon={isExporting ? <CircularProgress color="inherit" size={16} /> : undefined}
                            >
                                {isExporting ? 'Preparing...' : 'Export'}
                            </Button>
                        )}
                    </Grid>
                </Grid>
            }
        >
            {isFetching ? (
                <Loader />
            ) : data?.data?.data.length !== 0 ? (
                <CardContent>
                    <Grid container spacing={gridSpacing} marginBottom="15px">

                        <Grid item lg={3} sm={6}>
                            <Autocomplete
                                options={projectTypeOptions}
                                getOptionLabel={(option) => option.name}
                                value={projectTypeOptions.find((option) => option.name === projectTypeName) || null}
                                onChange={(event, value) => {
                                    setProjectTypeName(value?.name || '');
                                }}
                                renderInput={(params) => <TextField {...params} label="Select Project Type" variant="outlined" />}
                            />
                        </Grid>
                        <Grid item lg={3} sm={6}>
                            <Autocomplete
                                options={divisionData?.data as FieldOption[]}
                                getOptionLabel={(option) => option.field_name}
                                value={divisionData?.data.find((item: any) => item.id.toString() === divisionId) || null}
                                onChange={(event, value) => {
                                    if (value) {
                                        setDivisionId(value?.id.toString());
                                        setDistrictId('');
                                        setUpazilaId('');
                                        setUnionId('');
                                    }
                                }}
                                renderInput={(params) => <TextField {...params} label="Select division" variant="outlined" />}
                            />
                        </Grid>
                        <Grid item lg={3} sm={6}>
                            <Autocomplete
                                options={(distirctData?.data as FieldOption[]) ?? []}
                                getOptionLabel={(option) => option.field_name}
                                value={distirctData?.data.find((item: any) => item.id.toString() === districtId) || null}
                                onChange={(event, value) => {
                                    if (value) {
                                        setDistrictId(value?.id.toString());
                                        setUpazilaId('');
                                        setUnionId('');
                                    }
                                }}
                                renderInput={(params) => <TextField {...params} label="Select district" variant="outlined" />}
                            />
                        </Grid>
                        <Grid item lg={3} sm={6}>
                            <Autocomplete
                                options={(upazilaData?.data as FieldOption[]) ?? []}
                                getOptionLabel={(option) => option.field_name}
                                value={upazilaData?.data.find((item: any) => item.id.toString() === upazilaId) || null}
                                onChange={(event, value) => {
                                    if (value) {
                                        setUpazilaId(value?.id.toString());
                                        setUnionId('');
                                    }
                                }}
                                renderInput={(params) => <TextField {...params} label="Select upazila" variant="outlined" />}
                            />
                        </Grid>
                        <Grid item lg={3} sm={6}>
                            <Autocomplete
                                options={(unionData?.data as FieldOption[]) ?? []}
                                getOptionLabel={(option) => option.field_name}
                                value={unionData?.data.find((item: any) => item.id.toString() === unionId) || null}
                                onChange={(event, value) => {
                                    if (value) setUnionId(value?.id.toString());
                                }}
                                renderInput={(params) => <TextField {...params} label="Select union" variant="outlined" />}
                            />
                        </Grid>
                    </Grid>
                    <Grid container justifyContent="flex-end" alignItems="center" spacing={1}>
                        <Grid item>
                            <TextField
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                value={search}
                                id="outlined-basic"
                                label="search"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item>
                            <IconButton onClick={handleSearch} color="primary">
                                <SearchIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <TableContainer component={Paper}>
                        <Table sx={{ width: '100%' }} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left">#</TableCell>
                                    <TableCell align="left">Group Id</TableCell>
                                    <TableCell align="left">Group Name</TableCell>
                                    <TableCell align="left">Project Type</TableCell>
                                    <TableCell align="left">Group Type</TableCell>
                                    <TableCell align="left">Address</TableCell>
                                    <TableCell align="left">Formation Date</TableCell>
                                    <TableCell align="left">Member count</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data?.data?.data &&
                                    data.data.data.map((item: any, index: number) => (
                                        <TableRow key={index}>
                                            <TableCell align="left">{(page - 1) * row + index + 1}</TableCell>
                                            <TableCell align="left">{item.groupCode}</TableCell>
                                            <TableCell align="left">{item.name}</TableCell>
                                            <TableCell align="left">
                                                {item.projectTypeName || item.project_type_name || item.projectType || item.project_type || '-'}
                                            </TableCell>
                                            <TableCell align="left">{item.groupType}</TableCell>
                                            <TableCell align="left">{item.address}</TableCell>
                                            <TableCell align="left">{item.formationDate}</TableCell>
                                            <TableCell align="left">{item.memberCount}</TableCell>
                                            <TableCell>
                                                <Stack direction="row">
                                                    {/* Show Edit button only if verified permissions */}
                                                    {canEditGroup && (
                                                        <Tooltip placement="top" title="Edit">
                                                            <IconButton
                                                                color="primary"
                                                                sx={{
                                                                    color: theme.palette.warning.dark,
                                                                    borderColor: theme.palette.warning.main,
                                                                    '&:hover ': { background: theme.palette.warning.light }
                                                                }}
                                                                size="large"
                                                                onClick={() => handleEditClick(item.id)}
                                                                disabled={isNavigating}
                                                            >
                                                                <BorderColorTwoToneIcon sx={{ fontSize: '1.3rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Profile button - always visible if can view */}
                                                    {canViewGroup && (
                                                        <Tooltip placement="top" title="Profile">
                                                            <IconButton
                                                                color="primary"
                                                                sx={{
                                                                    color: theme.palette.success.dark,
                                                                    borderColor: theme.palette.success.main,
                                                                    '&:hover ': { background: theme.palette.success.light }
                                                                }}
                                                                size="large"
                                                                onClick={() => {
                                                                    navigate(`${profileUrl}/${item.id}`);
                                                                }}
                                                                disabled={isNavigating}
                                                            >
                                                                <PersonOutlineIcon sx={{ fontSize: '1.3rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Delete button - only for FAO_ADMIN */}
                                                    {deletePermission && (
                                                        <Tooltip placement="top" title="Delete">
                                                            <IconButton
                                                                sx={{
                                                                    color: 'red',
                                                                    borderColor: theme.palette.success.main,
                                                                    '&:hover ': { background: theme.palette.success.light }
                                                                }}
                                                                size="large"
                                                                onClick={() => handleDelete(item.id)}
                                                                disabled={isNavigating}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                        <Grid container spacing={2} alignItems="center" marginTop="10px">
                            <Grid item>
                                <Typography>Total Groups: {data?.data?.totalCount}</Typography>
                                <Typography>Total Members: {data?.data?.totalMemberCount}</Typography>
                            </Grid>
                            <Grid item>
                                <TextField
                                    select
                                    label="Rows per page"
                                    value={row}
                                    onChange={(e) => setRow(Number(e.target.value))}
                                    sx={{ width: '120px' }}
                                >
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={30}>30</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item>
                                <TextField
                                    type="number"
                                    label="Go to page"
                                    value={jumpPage}
                                    onChange={(e) => setJumpPage(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            const p = parseInt(jumpPage);
                                            const maxPage = Math.ceil(data?.data?.totalCount / row);
                                            if (p >= 1 && p <= maxPage) {
                                                setPage(p);
                                                setJumpPage('');
                                            }
                                        }
                                    }}
                                    inputProps={{ min: 1, max: Math.ceil(data?.data?.totalCount / row) }}
                                    sx={{ width: '120px' }}
                                />
                            </Grid>
                            <Grid item>
                                <Pagination
                                    count={Math.ceil(data?.data?.totalCount / row)}
                                    color="primary"
                                    page={page}
                                    onChange={handleChangePage}
                                />
                            </Grid>
                        </Grid>
                    </TableContainer>
                    {openModal && <GroupDeleteModal
                        open={openModal}
                        handleClose={handleCloseModal}
                        mutate={deleteGroup}
                        id={groupId}
                    />}
                </CardContent>
            ) : (
                <CardContent>
                    <Typography variant="body1">No Group data found</Typography>
                </CardContent>
            )}
        </MainCard>
    );
};

export default GroupRegister;
